import { formatDuration } from '@crm/shared';
import { useState } from 'react';
import { formatCurrency } from '@/lib/utils';
import { useServices } from '@/modules/catalog/api';
import { usePublicLanding } from '@/modules/landing/api';
import { useBookingMode } from '../../booking-mode';
import { SearchList } from './search-list';
import type { AppointmentWizard } from './use-appointment-wizard';

/** "1h30" e mais legivel que "90 min" numa lista longa. */
interface Choice {
  id: string;
  name: string;
  categoryName: string | null;
  price: number;
  durationMinutes: number;
  maxDiscountPercent: number;
  image: string | null;
}

/**
 * Passo 2: qual servico entra na comanda.
 *
 * No sistema a lista vem do catalogo; na pagina publica, do proprio conteudo
 * publicado - a mesma tela, so muda a fonte.
 */
export function StepService({ wizard }: { wizard: AppointmentWizard }) {
  const mode = useBookingMode();
  const [search, setSearch] = useState('');

  // Os mais vendidos primeiro enquanto a busca esta vazia; ao digitar, A-Z.
  const services = useServices(
    { page: 1, perPage: 50, search: search || undefined, sort: search ? 'name' : 'popular' },
    { enabled: mode === null },
  );
  const landing = usePublicLanding(mode?.slug);

  const term = search.trim().toLowerCase();
  const options: Choice[] = mode
    ? (landing.data?.services ?? [])
        .filter((service) => !term || service.name.toLowerCase().includes(term))
        .map((service) => ({
          id: service.id,
          name: service.name,
          categoryName: service.categoryName,
          price: service.price,
          durationMinutes: service.durationMinutes,
          maxDiscountPercent: 0,
          image: service.image,
        }))
        .sort((a, b) => (term ? a.name.localeCompare(b.name) : 0))
    : (services.data?.data ?? []).map((service) => ({
        id: service.id,
        name: service.name,
        categoryName: service.categoryName,
        price: service.price,
        durationMinutes: service.durationMinutes,
        maxDiscountPercent: service.maxDiscountPercent,
        image: service.images[0] ?? null,
      }));

  return (
    <div className="space-y-4">
      <p className="text-base text-muted-foreground">
        Toque no servico para escolher o profissional e o horario.
      </p>

      <SearchList
        placeholder="Buscar servico"
        isLoading={mode ? landing.isLoading : services.isLoading}
        emptyLabel={search ? 'Nenhum servico com esse nome.' : 'Nenhum servico cadastrado.'}
        onSearch={setSearch}
        items={options.map((service) => ({
          id: service.id,
          title: service.name,
          subtitle: service.categoryName ?? undefined,
          meta: `${formatCurrency(service.price)} - ${formatDuration(service.durationMinutes)}`,
          image: service.image,
        }))}
        onSelect={(id) => {
          const chosen = options.find((service) => service.id === id);
          if (!chosen) return;

          wizard.chooseService({
            serviceId: chosen.id,
            name: chosen.name,
            price: chosen.price,
            durationMinutes: chosen.durationMinutes,
            maxDiscountPercent: chosen.maxDiscountPercent,
            image: chosen.image,
          });
        }}
      />
    </div>
  );
}
